import { t } from "i18next";
import { ComponentProps } from "react";
import { ComputeFieldComponent } from "../computeFields";
import { ArtworkInBibliography } from "../types";
import { resolveBibliographieResumeLong } from "./resolveBibliographieResumeLong";
import { renderReproductionPart } from "./shared/bibliography-reproduction";
import { SeparatedList } from "./shared/utils";

/** Si texte libre est renseigné, seul le résumé biblio + texte libre est affiché ; sinon combinaisons cité / repr. / listé. */
export const BibliographyDescriptionCF: ComputeFieldComponent<
  Pick<
    ArtworkInBibliography,
    | "bibliography"
    | "oeuvresDansBibliographieDescriptionCitePage"
    | "oeuvresDansBibliographieDescriptionReproduitPage"
    | "oeuvresDansBibliographieDescriptionReproduitPlanche"
    | "oeuvresDansBibliographieDescriptionPlancheNonNumerotee"
    | "oeuvresDansBibliographieDescriptionListePage"
    | "oeuvresDansBibliographieDescriptionSousNumero"
    | "oeuvresDansBibliographieDescriptionReproductionCouverture"
    | "oeuvresDansBibliographieDescriptionReproductionCouleur"
    | "oeuvresDansBibliographieDescriptionNonPagine"
    | "oeuvresDansBibliographieDescriptionTexteLibre"
  >
> = ({ input, language, ...props }) => {
  const resumeBiblio = resolveBibliographieResumeLong(
    input.bibliography,
    language,
  );
  const texteLibre = (
    input.oeuvresDansBibliographieDescriptionTexteLibre ?? ""
  ).trim();

  if (texteLibre) {
    return (
      <SeparatedList
        {...props}
        lineBreak={false}
        language={language}
        commaSeparated
        input={[
          <span dangerouslySetInnerHTML={{ __html: resumeBiblio }} />,
          texteLibre,
        ]}
      />
    );
  }

  const coul = !!input.oeuvresDansBibliographieDescriptionReproductionCouleur;
  const couv =
    !!input.oeuvresDansBibliographieDescriptionReproductionCouverture;
  const nP = !!input.oeuvresDansBibliographieDescriptionNonPagine;
  const page = (
    input.oeuvresDansBibliographieDescriptionReproduitPage ?? ""
  ).trim();
  const planche = (
    input.oeuvresDansBibliographieDescriptionReproduitPlanche ?? ""
  ).trim();
  const plancheNonNum =
    !!input.oeuvresDansBibliographieDescriptionPlancheNonNumerotee;
  const citePage = (
    input.oeuvresDansBibliographieDescriptionCitePage ?? ""
  ).trim();
  const listePage = (
    input.oeuvresDansBibliographieDescriptionListePage ?? ""
  ).trim();
  const sousNumero = (
    input.oeuvresDansBibliographieDescriptionSousNumero ?? ""
  ).trim();

  const reproducePart = renderReproductionPart(
    { coul, couv, nP, page, planche, plancheNonNum },
    t,
  );

  const citePart = citePage && (
    <>
      {t("computeField.citePageAbbrege")} {citePage}
    </>
  );

  const listePart = listePage && (
    <>
      {t("computeField.listePageAbbrege")} {listePage}
      {sousNumero && (
        <>
          {" "}
          ({t("computeField.sousNumeroPrefix")}
          {sousNumero})
        </>
      )}
    </>
  );

  return (
    <SeparatedList
      {...props}
      lineBreak={false}
      language={language}
      commaSeparated
      input={[
        <span dangerouslySetInnerHTML={{ __html: resumeBiblio }} />,
        citePart,
        reproducePart,
        listePart,
      ]}
    />
  );
};

export const RelatedBibliographiesCF: ComputeFieldComponent<{
  relatedBibliographies?: ComponentProps<
    typeof BibliographyDescriptionCF
  >["input"][];
}> = ({ input, lineBreak = true, ...props }) => {
  if (
    !input.relatedBibliographies ||
    input.relatedBibliographies.length === 0
  ) {
    return <></>;
  }
  return (
    <SeparatedList
      {...props}
      input={input.relatedBibliographies.map((bibliography, index) => (
        <BibliographyDescriptionCF
          key={index}
          input={bibliography}
          {...props}
        />
      ))}
      lineBreak={lineBreak}
    />
  );
};
